import React, { useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  ShieldOff,
  ScanLine,
  Lock,
  Unlock,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import {
  fetchInboundDrums,
  scanInboundDrum,
  breakInboundSeal,
  fetchBatches,
} from '../../lib/api';

/**
 * Inbound Purity Shield — the supply-chain anti-fraud surface.
 *
 * Workflow:
 *   1. Scanner — pick a drum, "Scan Inbound Raw Material" verifies the
 *      tamper-seal on chain. GREEN if SEALED, RED if BROKEN/OPENED.
 *   2. Consumption Lock — "Break Seal & Start Mixing" only enables after
 *      a successful SEALED scan + active production batch chosen.
 *   3. Registry — full ledger view of every drum's current seal state.
 */
const InboundShield = () => {
  const [drums, setDrums] = useState([]);
  const [counts, setCounts] = useState({ SEALED: 0, OPENED: 0, BROKEN: 0 });
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scanner state
  const [pickedDrumId, setPickedDrumId] = useState('');
  const [activeBatchId, setActiveBatchId] = useState('');
  const [operator, setOperator] = useState('Atelier Heavener · Floor Manager');
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [breaking, setBreaking] = useState(false);
  const [breakResult, setBreakResult] = useState(null);
  const [error, setError] = useState('');

  const reload = () => {
    setLoading(true);
    Promise.all([fetchInboundDrums(), fetchBatches()])
      .then(([d, b]) => {
        setDrums(d.drums || []);
        setCounts(d.counts_by_status || { SEALED: 0, OPENED: 0, BROKEN: 0 });
        setBatches(b.batches || []);
      })
      .catch(() => setError('Could not reach the ledger.'))
      .finally(() => setLoading(false));
  };

  useEffect(reload, []);

  const sealedDrums = useMemo(
    () => drums.filter((d) => d.seal_status === 'SEALED'),
    [drums]
  );

  const onScan = async () => {
    if (!pickedDrumId) return;
    setScanning(true);
    setError('');
    setScanResult(null);
    setBreakResult(null);
    try {
      const res = await scanInboundDrum(pickedDrumId, operator || undefined);
      setScanResult(res);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Scan failed.');
    } finally {
      setScanning(false);
    }
  };

  const onBreakSeal = async () => {
    if (!scanResult || !scanResult.seal_ok || !activeBatchId) return;
    setBreaking(true);
    setError('');
    try {
      const res = await breakInboundSeal(
        scanResult.drum_id,
        activeBatchId,
        operator || undefined
      );
      setBreakResult(res);
      setScanResult(null);
      setPickedDrumId('');
      reload();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not break the seal.');
    } finally {
      setBreaking(false);
    }
  };

  return (
    <div data-testid="inbound-shield" className="space-y-12">
      {/* Header */}
      <header className="border-b border-[#C1D7E2] pb-6">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-2">
          Manufacturer Portal · Anti-Fraud Layer
        </p>
        <h2 className="font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-tight">
          The Inbound Purity Shield.
        </h2>
        <p className="mt-3 font-serif italic text-[#90B0BF] max-w-2xl">
          Every drum that leaves India carries a tamper-seal signed on chain.
          We verify it the moment it touches the floor — and only the verified
          ones may enter a batch.
        </p>
      </header>

      {/* Counts */}
      <section
        data-testid="inbound-counts"
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <CountCard
          icon={ShieldCheck}
          label="Sealed · ready"
          value={counts.SEALED}
          tone="green"
          testId="inbound-count-sealed"
        />
        <CountCard
          icon={ShieldAlert}
          label="Opened · committed"
          value={counts.OPENED}
          tone="neutral"
          testId="inbound-count-opened"
        />
        <CountCard
          icon={ShieldOff}
          label="Broken · transit fraud"
          value={counts.BROKEN}
          tone="red"
          testId="inbound-count-broken"
        />
      </section>

      {/* Scanner */}
      <section
        data-testid="inbound-scanner"
        className="bg-[#F4F8F9] border border-[#C1D7E2] p-8 md:p-10"
      >
        <div className="flex items-start gap-3 mb-6">
          <ScanLine size={20} strokeWidth={1.2} className="text-[#7C9CB1] mt-1" />
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
              Step 01 · Verify
            </p>
            <h3 className="font-serif text-2xl md:text-3xl text-[#7C9CB1] leading-tight">
              Scan Inbound Raw Material
            </h3>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <Field className="md:col-span-5" label="Drum at the loading bay">
            <select
              value={pickedDrumId}
              onChange={(e) => setPickedDrumId(e.target.value)}
              data-testid="inbound-drum-select"
              className="inbound-input"
            >
              <option value="">— Select a drum to scan —</option>
              {drums.map((d) => (
                <option key={d.drum_id} value={d.drum_id}>
                  {d.drum_id} · {d.ingredient_name} · {d.seal_status}
                </option>
              ))}
            </select>
          </Field>
          <Field className="md:col-span-5" label="Operator on the floor">
            <input
              type="text"
              value={operator}
              onChange={(e) => setOperator(e.target.value)}
              data-testid="inbound-operator-input"
              className="inbound-input"
            />
          </Field>
          <div className="md:col-span-2">
            <button
              type="button"
              onClick={onScan}
              disabled={!pickedDrumId || scanning}
              data-testid="inbound-scan-button"
              className="w-full bg-[#7C9CB1] text-[#F4F8F9] px-6 py-3.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#90B0BF] transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
            >
              {scanning ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ScanLine size={14} />
              )}
              {scanning ? 'Scanning' : 'Scan seal'}
            </button>
          </div>
        </div>

        {error && (
          <p
            data-testid="inbound-error"
            className="mt-4 text-sm text-[#7C9CB1] font-light"
          >
            <AlertTriangle size={14} className="inline mr-1" />
            {error}
          </p>
        )}

        {scanResult && <ScanResult result={scanResult} />}
      </section>

      {/* Consumption Lock */}
      {scanResult && scanResult.seal_ok && (
        <section
          data-testid="inbound-consume"
          className="bg-gradient-to-br from-[#C1D7E2]/50 to-[#AAC6D4]/30 border border-[#7C9CB1] p-8 md:p-10"
        >
          <div className="flex items-start gap-3 mb-6">
            <Unlock size={20} strokeWidth={1.2} className="text-[#7C9CB1] mt-1" />
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#7C9CB1]">
                Step 02 · Consume
              </p>
              <h3 className="font-serif text-2xl md:text-3xl text-[#7C9CB1] leading-tight">
                Break Seal &amp; Start Mixing
              </h3>
              <p className="mt-2 font-serif italic text-[#7C9CB1]/85">
                One-way action. The drum becomes permanently linked to the
                Active Production Batch.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <Field className="md:col-span-7" label="Active Production Batch">
              <select
                value={activeBatchId}
                onChange={(e) => setActiveBatchId(e.target.value)}
                data-testid="inbound-batch-select"
                className="inbound-input"
              >
                <option value="">— Choose the batch this drum will feed —</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.id} · {b.name} · {b.status}
                  </option>
                ))}
              </select>
            </Field>
            <div className="md:col-span-5">
              <button
                type="button"
                onClick={onBreakSeal}
                disabled={!activeBatchId || breaking}
                data-testid="inbound-break-button"
                className="w-full bg-[#7C9CB1] text-[#F4F8F9] px-6 py-3.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#90B0BF] transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {breaking ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Unlock size={14} />
                )}
                {breaking ? 'Committing' : 'Break Seal & Start Mixing'}
              </button>
            </div>
          </div>
        </section>
      )}

      {breakResult && (
        <section
          data-testid="inbound-break-receipt"
          className="bg-[#7C9CB1] text-[#F4F8F9] p-8 md:p-10 border border-[#7C9CB1]"
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 size={22} strokeWidth={1.3} className="text-[#C1D7E2] mt-1" />
            <div>
              <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#F4F8F9]/80">
                Seal broken · Inbound Purity Verified
              </p>
              <h4 className="font-serif text-2xl md:text-3xl mt-1 leading-tight">
                {breakResult.drum_id} → {breakResult.batch_id}
              </h4>
              <p className="mt-2 font-mono text-xs text-[#F4F8F9]/85">
                Committed at {breakResult.opened_at}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Drum Registry */}
      <section data-testid="inbound-registry" className="space-y-4">
        <header className="flex items-center gap-3">
          <Lock size={16} className="text-[#7C9CB1]" />
          <h3 className="font-serif text-2xl text-[#7C9CB1]">Drum Ledger</h3>
        </header>
        {loading ? (
          <div className="h-24 bg-[#C1D7E2]/40 animate-pulse" />
        ) : (
          <ul className="divide-y divide-[#C1D7E2]/60 border-t border-b border-[#C1D7E2]/60">
            {drums.map((d, i) => (
              <li
                key={d.drum_id}
                data-testid={`inbound-drum-row-${i}`}
                className="grid grid-cols-12 gap-3 py-4 items-center"
              >
                <span className="col-span-3 font-mono text-xs text-[#7C9CB1]">
                  {d.drum_id}
                </span>
                <span className="col-span-4 font-serif text-sm text-[#7C9CB1]">
                  {d.ingredient_name}
                  <span className="block text-[10px] italic text-[#90B0BF]">
                    {d.supplier} · {d.origin}
                  </span>
                </span>
                <span className="col-span-3 text-xs text-[#90B0BF]">
                  {d.opened_for_batch_id ? `→ ${d.opened_for_batch_id}` : '—'}
                </span>
                <span className="col-span-2 text-right">
                  <SealBadge status={d.seal_status} />
                </span>
              </li>
            ))}
          </ul>
        )}
        {!loading && sealedDrums.length === 0 && (
          <p
            data-testid="inbound-empty"
            className="font-serif italic text-[#90B0BF] py-6"
          >
            No sealed drums waiting at the loading bay.
          </p>
        )}
      </section>

      <style>{`
        .inbound-input {
          width: 100%;
          background: #fff;
          border: 1px solid rgba(124,156,177,0.35);
          color: #7C9CB1;
          font-family: 'Outfit', sans-serif;
          font-weight: 300;
          padding: 12px 14px;
          font-size: 14px;
        }
        .inbound-input:focus {
          outline: none;
          border-color: #7C9CB1;
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
      {label}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);

const CountCard = ({ icon: Icon, label, value, tone, testId }) => {
  const palette = {
    green:   { fg: '#16614a', bg: 'rgba(193, 215, 226, 0.55)', ring: '#7C9CB1' },
    neutral: { fg: '#7C9CB1', bg: '#F4F8F9',                   ring: '#C1D7E2' },
    red:     { fg: '#9c2a2a', bg: 'rgba(214, 196, 196, 0.65)', ring: '#9c2a2a' },
  }[tone];
  return (
    <div
      data-testid={testId}
      className="border p-6"
      style={{ borderColor: palette.ring, background: palette.bg }}
    >
      <div className="flex items-center gap-3 mb-3">
        <Icon size={18} strokeWidth={1.3} style={{ color: palette.fg }} />
        <p className="font-mono text-[10px] tracking-[0.28em] uppercase" style={{ color: palette.fg }}>
          {label}
        </p>
      </div>
      <p className="font-serif text-5xl font-light leading-none" style={{ color: palette.fg }}>
        {value}
      </p>
    </div>
  );
};

const SealBadge = ({ status }) => {
  const map = {
    SEALED: { fg: '#16614a', bg: 'rgba(180, 215, 195, 0.6)', icon: ShieldCheck },
    OPENED: { fg: '#7C9CB1', bg: 'rgba(193, 215, 226, 0.6)', icon: ShieldAlert },
    BROKEN: { fg: '#9c2a2a', bg: 'rgba(220, 198, 198, 0.7)', icon: ShieldOff },
  };
  const cfg = map[status] || map.OPENED;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-2 px-3 py-1 font-mono text-[10px] tracking-[0.22em] uppercase"
      style={{ color: cfg.fg, background: cfg.bg, border: `1px solid ${cfg.fg}` }}
    >
      <Icon size={12} strokeWidth={1.5} />
      {status}
    </span>
  );
};

const ScanResult = ({ result }) => {
  const ok = result.seal_ok;
  const fg = ok ? '#16614a' : '#9c2a2a';
  const bg = ok ? 'rgba(180, 215, 195, 0.4)' : 'rgba(220, 198, 198, 0.55)';
  const Icon = ok ? ShieldCheck : ShieldOff;
  return (
    <div
      data-testid="inbound-scan-result"
      className="mt-8 border p-6 md:p-7 flex items-start gap-4"
      style={{ borderColor: fg, background: bg }}
    >
      <Icon size={28} strokeWidth={1.3} style={{ color: fg }} />
      <div>
        <p className="font-mono text-[10px] tracking-[0.32em] uppercase mb-2" style={{ color: fg }}>
          {ok ? 'Seal Verified · Safe to Consume' : 'Seal Compromised · Quarantine'}
        </p>
        <h4 className="font-serif text-2xl md:text-3xl text-[#7C9CB1] leading-tight">
          {result.drum_id}
          <span className="block text-base font-normal italic text-[#90B0BF] mt-1">
            {result.drum.ingredient_name} · {result.drum.supplier}
          </span>
        </h4>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 font-mono text-[11px] text-[#7C9CB1]">
          <span>Origin · {result.drum.origin}</span>
          <span>Capacity · {result.drum.capacity_kg} kg</span>
          <span>Verified · {result.verified_at}</span>
        </div>
        {result.note && (
          <p className="mt-3 font-mono text-[11px]" style={{ color: fg }}>
            {result.note}
          </p>
        )}
      </div>
    </div>
  );
};

export default InboundShield;
